import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../utils/api'

function CategoryManager() {
  const { getToken } = useAuth()
  const [categories, setCategories] = useState([])
  const [newCategory, setNewCategory] = useState({ name: '', color: '#3B82F6', icon: '💰' })
  const [newSubcategory, setNewSubcategory] = useState({ category_id: '', name: '' })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      const data = await api.get('/api/categories', getToken)
      setCategories(data)
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleAddCategory = async (e) => {
    e.preventDefault()
    
    if (!newCategory.name) {
      alert('Please enter a category name')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/categories', newCategory, getToken)
      setNewCategory({ name: '', color: '#3B82F6', icon: '💰' })
      loadCategories()
    } catch (error) {
      console.error('Error adding category:', error)
      alert('Failed to add category')
    }
    setLoading(false)
  }

  const handleAddSubcategory = async (e) => {
    e.preventDefault()
    
    if (!newSubcategory.category_id || !newSubcategory.name) {
      alert('Please select a category and enter a subcategory name')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/subcategories', {
        category_id: parseInt(newSubcategory.category_id),
        name: newSubcategory.name
      }, getToken)
      
      setNewSubcategory({ category_id: '', name: '' })
      loadCategories()
    } catch (error) {
      console.error('Error adding subcategory:', error)
      alert('Failed to add subcategory')
    }
    setLoading(false)
  }

  const handleDeleteCategory = async (id) => {
    if (!confirm('Delete this category? All associated expenses will also be deleted!')) return

    try {
      await api.delete(`/api/categories?id=${id}`, getToken)
      loadCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
    }
  }

  const handleDeleteSubcategory = async (id) => {
    if (!confirm('Delete this subcategory?')) return

    try {
      await api.delete(`/api/subcategories?id=${id}`, getToken)
      loadCategories()
    } catch (error) {
      console.error('Error deleting subcategory:', error)
    }
  }

  const commonIcons = ['💰', '🏠', '🚗', '🍔', '💡', '⚕️', '🎮', '🛍️', '📚', '💅', '🛡️', '💳', '📦', '✈️', '🎬', '🏋️', '🐕', '📱', '🎨', '🔧']

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Category Management</h2>

      {/* Add New Category */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Category</h3>
        <form onSubmit={handleAddCategory} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Entertainment, Healthcare"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="w-full h-10 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {commonIcons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setNewCategory({ ...newCategory, icon })}
                  className={`text-2xl p-2 rounded-lg border-2 transition-all ${
                    newCategory.icon === icon 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={newCategory.icon}
              onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
              placeholder="Or type your own emoji"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Adding...' : 'Add Category'}
          </button>
        </form>
      </div>

      {/* Add New Subcategory */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Subcategory</h3>
        <form onSubmit={handleAddSubcategory} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Category <span className="text-red-500">*</span>
              </label>
              <select
                value={newSubcategory.category_id}
                onChange={(e) => setNewSubcategory({ ...newSubcategory, category_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newSubcategory.name}
                onChange={(e) => setNewSubcategory({ ...newSubcategory, name: e.target.value })}
                placeholder="e.g., Netflix, Gym Membership"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Adding...' : 'Add Subcategory'}
          </button>
        </form>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Categories</h3>
        {categories.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No categories yet.</p>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{category.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{category.name}</h4>
                      <div 
                        className="w-16 h-3 rounded mt-1" 
                        style={{ backgroundColor: category.color }}
                      ></div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    🗑️
                  </button>
                </div>

                {category.subcategories && category.subcategories.length > 0 && (
                  <div className="ml-12 space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase">Subcategories:</p>
                    {category.subcategories.map((sub) => (
                      <div key={sub.id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{sub.name}</span>
                        <button
                          onClick={() => handleDeleteSubcategory(sub.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CategoryManager

